import React, { useState } from "react";
import ThinkyTTS from "./ThinkyTTS";

const TABS = [
  "ReadThinky (TTS)",
  "Agents",
  "Music",
  "ListenThinky (STT)",
  "Dubbing",
  "Voice Cloning",
];

const READ_MODES = [
  "Narrate",
  "2 speakers dialogue",
  "Announcer",
  "An old story",
  "Provide customer support",
];

const ThinkySpeaksHome = () => {
  const [activeTab, setActiveTab] = useState("ReadThinky (TTS)");
  const [activeMode, setActiveMode] = useState("Narrate a story");
  const [text, setText] = useState("");
  const [style, setStyle] = useState("neutral");
  const [language, setLanguage] = useState("en");

  const renderReadThinky = () => (
    <>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={`Enter text to ${activeMode.toLowerCase()}...`}
        rows={5}
        className="w-full max-w-md mb-4 p-3 rounded-lg border border-gray-600 bg-gray-900 text-white text-base resize-y focus:outline-none focus:ring-2 focus:ring-blue-500"
      />

      <div className="grid grid-cols-3 lg:grid-cols-5 gap-2 mb-1 place-items-center">
        {READ_MODES.map((mode) => (
          <button
            key={mode}
            onClick={() => setActiveMode(mode)}
            className={`px-3 py-1.5 rounded-sm text-xs md:text-sm transition-all
              ${
                activeMode === mode
                  ? "bg-blue-600 text-white"
                  : "bg-gray-700 text-gray-400 hover:bg-gray-600"
              }`}
          >
            {mode}
          </button>
        ))}
      </div>

      <div className="flex gap-3 mb-2">
        <select
          value={style}
          onChange={(e) => setStyle(e.target.value)}
          className="p-2 rounded-lg bg-gray-800 text-white border border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="neutral">Neutral</option>
          <option value="happy">Happy</option>
          <option value="sad">Sad</option>
          <option value="excited">Excited</option>
        </select>

        <select
          value={language}
          onChange={(e) => setLanguage(e.target.value)}
          className="p-2 rounded-lg bg-gray-800 text-white border border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="en">English</option>
          <option value="hi">Hindi</option>
        </select>
      </div>

      <ThinkyTTS
        text={text}
        style={style}
        language={language}
        mode={activeMode}
      />
    </>
  );

  const renderThinkyAgents = () => {
    return (
      <div className="w-full max-w-4xl text-gray-300 space-y-8">
        <div className="text-center bg-gray-900 border border-gray-700 rounded-xl p-6">
          <h3 className="text-2xl font-semibold text-white mb-2">
            Thinky Agents
          </h3>
          <p className="text-lg">
            Thinky Agents will orchestrate intelligent workflows and autonomous reasoning.
          </p>
          <p className="text-sm text-gray-500 mt-2">(Coming soon)</p>
        </div>
        <section>
          <h4 className="text-xl font-semibold text-white mb-3">General</h4>
          <ul className="grid md:grid-cols-2 gap-4">
            <AgentCard
              title="Product Overview"
              description="Discover Thinky Speaking Agents"
            />
            <AgentCard
              title="Integrations"
              description="Connect agents to telephony and tools"
            />
            <AgentCard
              title="Customer Stories"
              description="Learn how customers benefit from agents"
            />
            <AgentCard
              title="Trust and Reliability"
              description="Deploy safe, compliant AI agents"
            />
            <AgentCard
              title="Forward Deployed Engineers"
              description="Launch agents that deliver value faster"
            />
          </ul>
        </section>
        <section>
          <h4 className="text-xl font-semibold text-white mb-3">Industries</h4>
          <ul className="grid md:grid-cols-2 gap-4">
            <AgentCard
              title="Telecommunications"
              description="Smarter conversations at scale"
            />
            <AgentCard
              title="Financial Services"
              description="Deliver secure, reliable service"
            />
            <AgentCard
              title="Healthcare"
              description="Streamline patient and provider operations"
            />
            <AgentCard
              title="Technology"
              description="Drive growth with conversational agents"
            />
            <AgentCard
              title="Retail"
              description="Increase sales and customer loyalty"
            />
          </ul>
        </section>
        <section>
          <h4 className="text-xl font-semibold text-white mb-3">Use Cases</h4>
          <ul className="grid md:grid-cols-2 gap-4">
            <AgentCard
              title="Customer Support"
              description="Resolve inquiries with AI agents"
            />
            <AgentCard
              title="Lead Qualification"
              description="Turn more leads into revenue"
            />
            <AgentCard
              title="AI Receptionist"
              description="Streamline business operations"
            />
            <AgentCard
              title="Outbound"
              description="Scale outreach efficiently"
            />
          </ul>
        </section>

      </div>
    );
  };

  const AgentCard = ({ title, description }) => (
    <li className="bg-gray-900 border border-gray-700 rounded-lg p-4 hover:border-blue-500 transition">
      <h5 className="text-white font-medium mb-1">{title}</h5>
      <p className="text-sm text-gray-400">{description}</p>
    </li>
  );

  const renderThinkyMusic = () => {
    return (
      <div className="w-full max-w-5xl text-gray-300 space-y-10">

        {/* Intro */}
        <div className="text-center bg-gray-900 border border-gray-700 rounded-xl p-6">
          <h3 className="text-2xl font-semibold text-white mb-2">
            🎵 Thinky Music
          </h3>
          <p className="text-lg">
            Generate, remix, and adapt music using AI.
          </p>
          <p className="text-sm text-gray-500 mt-2">(Coming soon)</p>
        </div>

        {/* Generate a Song */}
        <section className="bg-gray-900 border border-gray-700 rounded-xl p-6 space-y-4">
          <h4 className="text-xl font-semibold text-white flex items-center gap-2">
            🎶 Generate a Song
          </h4>

          <div className="bg-gray-800 border border-gray-700 rounded-lg p-4 text-sm text-gray-300">
            <p className="mb-2 text-gray-400">Prompt</p>
            <p>
              Generate a high BPM afrobeat track with traditional drums and modern synthesizers
            </p>
          </div>

          <button
            disabled
            className="px-5 py-2 rounded-lg bg-blue-600 text-white opacity-70 cursor-not-allowed flex items-center gap-2"
          >
            ▶️ Generate
          </button>
        </section>

        {/* Our Picks */}
        <section>
          <h4 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
            ⭐ Our Picks
          </h4>

          <div className="grid md:grid-cols-2 gap-5">

            <MusicCard
              icon="🎬"
              title="Cinematic Scores"
              tags="Orchestral · Epic · Triumphant · Fantasy"
            />

            <MusicCard
              icon="🎤"
              title="Conscious Hip Hop"
              tags="Conscious Rap · Jazzy · Experimental"
            />

            <MusicCard
              icon="🏢"
              title="Corporate Music"
              tags="Downtempo · Chill · Warm · Modern"
            />

            <MusicCard
              icon="🌙"
              title="Dreamy Indie Rock"
              tags="Slow Tempo · Atmospheric · Dreamy"
            />

            <MusicCard
              icon="🎮"
              title="Game Soundtracks"
              tags="Mysterious · Jungle · Tribal · Rhythmic"
            />

            <MusicCard
              icon="🌍"
              title="Multilingual Vocals"
              tags="Passionate · Raw · Spanish · A Capella"
            />

          </div>
        </section>

      </div>
    );
  };

  const MusicCard = ({ icon, title, tags }) => (
    <div className="bg-gray-900 border border-gray-700 rounded-xl p-5 hover:border-blue-500 transition">
      <div className="text-3xl mb-2">{icon}</div>
      <h5 className="text-white font-medium mb-1">{title}</h5>
      <p className="text-sm text-gray-400">{tags}</p>
    </div>
  );

  const renderListenThinkySTT = () => {
    return (
      <div className="w-full max-w-4xl text-gray-300 space-y-8">

        {/* Header */}
        <div className="text-center bg-gray-900 border border-gray-700 rounded-xl p-6">
          <h3 className="text-2xl font-semibold text-white mb-2">
            🎧 ListenThinky (STT)
          </h3>
          <p className="text-lg">
            Convert speech to text with contextual intelligence
          </p>
          <p className="text-sm text-gray-500 mt-2">(Coming soon)</p>
        </div>

        {/* Transcript Types */}
        <section className="grid md:grid-cols-3 gap-4">
          <STTCard
            icon="📞"
            title="Conference Call"
            subtitle="Multi-speaker conversations"
          />
          <STTCard
            icon="👥"
            title="Interview"
            subtitle="One-to-one structured dialogue"
          />
          <STTCard
            icon="🩺"
            title="Medical Interview"
            subtitle="Clinical and patient conversations"
          />
        </section>

        {/* Upload */}
        <section className="bg-gray-900 border border-gray-700 rounded-xl p-6 text-center space-y-4">
          <div className="text-4xl">⬆️</div>
          <h4 className="text-lg font-semibold text-white">
            Upload Audio File
          </h4>
          <p className="text-sm text-gray-400">
            Upload WAV, MP3, or M4A files to generate transcripts
          </p>

          <button
            disabled
            className="px-5 py-2 rounded-lg bg-blue-600 text-white opacity-70 cursor-not-allowed"
          >
            Upload file
          </button>
        </section>

        {/* Empty State */}
        <section className="bg-gray-900 border border-dashed border-gray-700 rounded-xl p-8 text-center">
          <div className="text-5xl mb-3">📄</div>
          <h4 className="text-lg font-semibold text-white">
            No Transcript Available
          </h4>
          <p className="text-sm text-gray-400 mt-2">
            No words were detected in this audio file
          </p>
        </section>

      </div>
    );
  };

  const STTCard = ({ icon, title, subtitle }) => (
    <div className="bg-gray-900 border border-gray-700 rounded-xl p-5 hover:border-blue-500 transition text-center">
      <div className="text-3xl mb-2">{icon}</div>
      <h5 className="text-white font-medium">{title}</h5>
      <p className="text-sm text-gray-400 mt-1">{subtitle}</p>
    </div>
  );

  const renderThinkyDubbing = () => {
    return (
      <div className="w-full max-w-5xl text-gray-300 space-y-10">

        {/* Header */}
        <div className="text-center bg-gray-900 border border-gray-700 rounded-xl p-6">
          <h3 className="text-2xl font-semibold text-white mb-2">
            🌐 Thinky Dubbing
          </h3>
          <p className="text-lg">
            AI-powered multilingual voice replacement with lip & timing sync
          </p>
          <p className="text-sm text-gray-500 mt-2">(Coming soon)</p>
        </div>

        {/* Step Flow */}
        <section className="grid md:grid-cols-4 gap-4 text-center">
          <DubbingStep icon="⬆️" title="Upload Video / Audio" />
          <DubbingStep icon="🗣️" title="Detect Speakers" />
          <DubbingStep icon="🌍" title="Select Target Language" />
          <DubbingStep icon="🎬" title="Sync & Dub" />
        </section>

        {/* Input Area */}
        <section className="bg-gray-900 border border-gray-700 rounded-xl p-6 space-y-5">
          <h4 className="text-xl font-semibold text-white flex items-center gap-2">
            🎞️ Source Media
          </h4>

          <div className="border border-dashed border-gray-700 rounded-lg p-6 text-center space-y-3">
            <div className="text-4xl">📁</div>
            <p className="text-sm text-gray-400">
              Upload MP4, MOV, MP3, or WAV
            </p>

            <button
              disabled
              className="px-5 py-2 rounded-lg bg-blue-600 text-white opacity-70 cursor-not-allowed"
            >
              Upload media
            </button>
          </div>
        </section>

        {/* Dubbing Options */}
        <section className="grid md:grid-cols-3 gap-4">

          <DubbingOption
            icon="🌍"
            title="Target Language"
            desc="Choose the language to dub into"
            value="English, Hindi, Spanish..."
          />

          <DubbingOption
            icon="🎭"
            title="Voice Style"
            desc="Match emotion & tone"
            value="Neutral · Expressive · Narrative"
          />

          <DubbingOption
            icon="🕒"
            title="Sync Mode"
            desc="Control timing & lip alignment"
            value="Auto Sync (Recommended)"
          />

        </section>

        {/* Preview */}
        <section className="bg-gray-900 border border-gray-700 rounded-xl p-6 text-center space-y-4">
          <div className="text-4xl">🎧</div>
          <h4 className="text-lg font-semibold text-white">
            Dubbed Preview
          </h4>
          <p className="text-sm text-gray-400">
            Preview will appear here after processing
          </p>

          <button
            disabled
            className="px-6 py-2 rounded-lg bg-green-600 text-white opacity-70 cursor-not-allowed"
          >
            ▶️ Generate Dub
          </button>
        </section>

      </div>
    );
  };

const DubbingStep = ({ icon, title }) => (
  <div className="bg-gray-900 border border-gray-700 rounded-xl p-4">
    <div className="text-3xl mb-2">{icon}</div>
    <p className="text-sm text-white">{title}</p>
  </div>
);

const DubbingOption = ({ icon, title, desc, value }) => (
    <div className="bg-gray-900 border border-gray-700 rounded-xl p-5 space-y-2">
      <div className="text-2xl">{icon}</div>
      <h5 className="text-white font-medium">{title}</h5>
      <p className="text-sm text-gray-400">{desc}</p>
      <div className="text-xs text-gray-500 bg-gray-800 rounded px-2 py-1 inline-block">
        {value}
      </div>
    </div>
  );

  const renderThinkyVoiceCloning = () => {
    return (
      <div className="w-full max-w-5xl text-gray-300 space-y-10">

        {/* Header */}
        <div className="text-center bg-gray-900 border border-gray-700 rounded-xl p-6">
          <h3 className="text-2xl font-semibold text-white mb-2">
            🧬 Thinky Voice Cloning
          </h3>
          <p className="text-lg">
            Replicate unique voices with consent, safety, and control
          </p>
          <p className="text-sm text-gray-500 mt-2">(Coming soon)</p>
        </div>

        {/* Ethics & Consent */}
        <section className="bg-gray-900 border border-yellow-600 rounded-xl p-6 space-y-3">
          <h4 className="text-lg font-semibold text-yellow-400 flex items-center gap-2">
            🛡️ Ethical Safeguards
          </h4>
          <ul className="list-disc list-inside text-sm text-gray-300 space-y-1">
            <li>Explicit consent required from voice owner</li>
            <li>Watermarked AI-generated audio</li>
            <li>Restricted impersonation scenarios</li>
            <li>Usage logging & traceability</li>
          </ul>
        </section>

        {/* Upload Voice Samples */}
        <section className="bg-gray-900 border border-gray-700 rounded-xl p-6 space-y-4">
          <h4 className="text-xl font-semibold text-white flex items-center gap-2">
            🎙️ Voice Samples
          </h4>

          <div className="border border-dashed border-gray-700 rounded-lg p-6 text-center space-y-3">
            <div className="text-4xl">📤</div>
            <p className="text-sm text-gray-400">
              Upload 3–10 minutes of clean voice audio
            </p>

            <button
              disabled
              className="px-5 py-2 rounded-lg bg-blue-600 text-white opacity-70 cursor-not-allowed"
            >
              Upload voice samples
            </button>
          </div>
        </section>

        {/* Voice Profile Settings */}
        <section className="grid md:grid-cols-3 gap-4">

          <VoiceOption
            icon="🧑"
            title="Voice Identity"
            desc="Name & tag the voice profile"
            value="Professional · Narrator · Character"
          />

          <VoiceOption
            icon="🎭"
            title="Expression Range"
            desc="Control emotional flexibility"
            value="Neutral · Expressive · Dramatic"
          />

          <VoiceOption
            icon="🔒"
            title="Usage Scope"
            desc="Define where this voice can be used"
            value="Private · Project-only"
          />

        </section>

        {/* Preview */}
        <section className="bg-gray-900 border border-gray-700 rounded-xl p-6 text-center space-y-4">
          <div className="text-4xl">🎧</div>
          <h4 className="text-lg font-semibold text-white">
            Voice Preview
          </h4>
          <p className="text-sm text-gray-400">
            Preview generated voice samples here
          </p>

          <button
            disabled
            className="px-6 py-2 rounded-lg bg-green-600 text-white opacity-70 cursor-not-allowed"
          >
            ▶️ Create Voice
          </button>
        </section>

      </div>
    );
  };

  const VoiceOption = ({ icon, title, desc, value }) => (
    <div className="bg-gray-900 border border-gray-700 rounded-xl p-5 space-y-2">
      <div className="text-2xl">{icon}</div>
      <h5 className="text-white font-medium">{title}</h5>
      <p className="text-sm text-gray-400">{desc}</p>
      <div className="text-xs text-gray-500 bg-gray-800 rounded px-2 py-1 inline-block">
        {value}
      </div>
    </div>
  );

  const renderContent = () => {
    switch (activeTab) {
      case "ReadThinky (TTS)":
        return renderReadThinky();

      case "Agents":
        return renderThinkyAgents();

      case "Music":
        return renderThinkyMusic();

      case "ListenThinky (STT)":
        return renderListenThinkySTT();

      case "Dubbing":
        return renderThinkyDubbing();

      case "Voice Cloning":
        return renderThinkyVoiceCloning();

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-black text-white flex flex-col items-center p-5 box-border">
      <h2 className="text-lg lg:text-xl font-semibold mb-4 text-center">
        Thinky Audio Studio
      </h2>

      <div className="grid grid-cols-3 lg:grid-cols-6 gap-1 mb-2 place-items-center">
        {TABS.map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-1 py-1 rounded-sm text-xs lg:text-sm transition-all
              ${
                activeTab === tab
                  ? "bg-blue-600 text-white"
                  : "bg-gray-800 text-gray-300 hover:bg-gray-700"
              }`}
          >
            {tab}
          </button>
        ))}
      </div>

      <div className="w-full flex flex-col items-center">
        {renderContent()}
      </div>
    </div>
  );
};

const Info = ({ text }) => (
  <div className="max-w-md text-center text-gray-300 bg-gray-900 border border-gray-700 rounded-xl p-6">
    <p className="text-lg">{text}</p>
    <p className="text-sm text-gray-500 mt-3">(Coming soon)</p>
  </div>
);

export default ThinkySpeaksHome;
